import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Input,
  Progress,
  Segmented,
  Space,
  Table,
  Tag,
  Tooltip,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { CalendarOutlined, SearchOutlined } from "@ant-design/icons";
import {
  ManpowerSummaryRow,
  manpowerRowKey,
  manpowerGroupLabel,
  manpowerSubGroupLabel,
  buildGroupFamilyMap,
  groupFamily,
  compareGroupLabels,
  computeParetoAbc,
  fmtINR,
  fmtINRCompact,
  fmtQty,
  num,
} from "./manpowerShared";

// Unified labour register across both sources: rank (by cost, fixed from the
// API's DESC order and computed BEFORE filtering so ranks stay global), item
// name + code, source tag (PRW blue / Others orange), quantities, avg rate,
// cost with % share of the grand total. Segmented source filter + search +
// sort + pagination are all client-side.
//
// GROUP MODE nests the register two levels deep by the BOQ norm group
// (B3 Underground Excavation -> B3.2 Excavation of Hydraulic tunnels -> items),
// off the level1Code/groupCode the backend stamps on each row. Two levels
// because one would be too coarse: project 112's 65 items sit under just 6
// heads. Rows the dictionary cannot place — every OTHERS row, since task
// resources are labour types rather than norms — fall back to their source
// bucket instead of a meaningless "Ungrouped".

const INK = "#022c57";
const MUTED = "#8094ae";
const DASH = "#c9d2e3";

// The % Share cell carries three facts without three columns:
//   LENGTH  the row's share of total manpower cost
//   COLOUR  which BOQ norm group it belongs to — the SAME family the donut's
//           Norm Group ring paints it, so one group is one colour page-wide
//   LETTER  its ABC class, the cumulative-concentration fact the Pareto plots
//
// Colour is identity here, not magnitude, so a categorical palette is correct
// (length already carries the magnitude). Group rows take their family's base
// step and their items a lighter step of the same family, which is the inner /
// outer ring relationship of the donut expressed as a table.
const SHARE_TRACK = "#eef1f6";

const ABC_HINT: Record<"A" | "B" | "C", string> = {
  A: "Class A — among the costliest items making up the first ~80% of manpower spend. Tightest rate and productivity control.",
  B: "Class B — the next ~15% of spend (up to 95%).",
  C: "Class C — the long tail, the last ~5% of spend.",
};

type SourceFilter = "All" | "PRW" | "Others";
type ViewMode = "Group" | "List";

interface TableRow extends ManpowerSummaryRow {
  key: string;
  rank: number;
  qtyNum: number;
  rateNum: number;
  costNum: number;
  isGroup?: boolean;
  /** Children span more than one UOM, so per-unit columns cannot roll up. */
  mixedUom?: boolean;
  /** Leaf count beneath this group, at any depth. */
  leafCount?: number;
  /**
   * The LEVEL-1 group this row belongs to, so a sub-group row takes its
   * parent's colour family rather than a family of its own.
   */
  familyLabel?: string;
  children?: TableRow[];
}

// Rolls a set of leaf rows up into the shape of one row, so group headers reuse
// the SAME cell renderers as the items beneath them.
//
// The rule that keeps this honest: ₹ TOTALS ARE ALWAYS ADDITIVE, PER-UNIT
// FIGURES ARE NOT. Summing ₹ across a Cum item and a Kg item is fine; summing
// their quantities or averaging their rates is not — so both are gated on the
// group sharing one UOM and blank out otherwise rather than inventing a number.
// Live shape on project 112: B3/B9/B5/B11 are single-UOM, B4 spans four units
// (Meter, MT, Cum, Kg) and B7 spans three.
const aggregate = (leaves: TableRow[]) => {
  const uoms = new Set(leaves.map((row) => row.uomName || "").filter(Boolean));
  const mixedUom = uoms.size > 1;

  const totalQty = leaves.reduce((sum, row) => sum + row.qtyNum, 0);
  const totalCost = leaves.reduce((sum, row) => sum + row.costNum, 0);

  return {
    uomName: mixedUom ? "" : Array.from(uoms)[0] || "",
    mixedUom,
    qtyNum: mixedUom ? NaN : totalQty,
    // Σcost / Σqty — identical to how each item's own avgRate is derived.
    rateNum: mixedUom || totalQty <= 0 ? NaN : totalCost / totalQty,
    costNum: totalCost,
    // taskCount is COUNT(DISTINCT taskId) PER ITEM and items in a group
    // routinely share tasks, so a sum would over-count. Left undefined; the
    // renderer shows a dash on group rows.
    taskCount: undefined,
    lineCount: leaves.reduce((sum, row) => sum + num(row.lineCount), 0),
    leafCount: leaves.length,
  };
};

const ManpowerSummaryTable: React.FC<{
  items: ManpowerSummaryRow[];
  grandTotal: number;
  // Drill-down: clicking an item's task count opens the phase → activity →
  // task breakdown drawer for that labour item.
  onTasksClick?: (row: ManpowerSummaryRow) => void;
  // Clicking the item NAME opens its month-by-month schedule instead.
  onItemClick?: (row: ManpowerSummaryRow) => void;
}> = ({ items, grandTotal, onTasksClick, onItemClick }) => {
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("All");
  // Grouped is the default view across every norm-group tree in the app — the
  // group headers ARE the overview, and List is the escape hatch.
  const [viewMode, setViewMode] = useState<ViewMode>("Group");

  const rows = useMemo<TableRow[]>(
    () =>
      items.map((m, index) => ({
        ...m,
        key: manpowerRowKey(m),
        rank: index + 1,
        qtyNum: num(m.totalQuantity),
        rateNum: num(m.avgRate),
        costNum: num(m.totalCost),
      })),
    [items]
  );

  // Both built over EVERY item, never the filtered set: a row's colour and its
  // class are properties of the row, so filtering to PRW or typing in the
  // search box must not repaint the rows that survive. Same reasoning that
  // keeps `rank` global.
  const familyByGroup = useMemo(() => buildGroupFamilyMap(rows), [rows]);

  const abcByKey = useMemo(() => {
    const pareto = computeParetoAbc(
      rows.map((row) => ({ ...row, cost: row.costNum }))
    );
    const map = new Map<string, "A" | "B" | "C">();
    pareto.points.forEach((point) => {
      map.set(point.key, pareto.classOf(point.rank));
    });
    return map;
  }, [rows]);

  const filteredRows = useMemo(() => {
    let result = rows;
    if (sourceFilter !== "All") {
      const source = sourceFilter === "PRW" ? "PRW" : "OTHERS";
      result = result.filter((row) => row.source === source);
    }
    const q = search.trim().toLowerCase();
    if (!q) return result;
    return result.filter(
      (row) =>
        (row.itemName || "").toLowerCase().includes(q) ||
        (row.itemCode || "").toLowerCase().includes(q)
    );
  }, [rows, search, sourceFilter]);

  // Two-level tree. Group rows are always aggregated over LEAF rows, never over
  // nested group rows, so a two-level branch cannot double-count its subtotals.
  const groupedRows = useMemo<TableRow[]>(() => {
    const makeGroup = (
      label: string,
      leaves: TableRow[],
      keyPrefix: string,
      familyLabel: string
    ): TableRow => {
      const sorted = [...leaves].sort((a, b) => b.costNum - a.costNum);
      return {
        key: `${keyPrefix}::${label}`,
        isGroup: true,
        source: "PRW",
        rank: 0,
        itemName: label,
        familyLabel,
        // aggregate() supplies uomName/mixedUom/qtyNum/rateNum/costNum/
        // taskCount/lineCount/leafCount.
        ...aggregate(sorted),
        children: sorted,
      };
    };

    const byLevel1 = new Map<string, TableRow[]>();
    filteredRows.forEach((row) => {
      const label = manpowerGroupLabel(row);
      byLevel1.set(label, [...(byLevel1.get(label) || []), row]);
    });

    return Array.from(byLevel1.entries())
      .map(([label, leaves]) => {
        const group = makeGroup(label, leaves, "g1", label);

        const bySub = new Map<string, TableRow[]>();
        let resolved = 0;
        leaves.forEach((row) => {
          const sub = manpowerSubGroupLabel(row);
          if (sub) resolved += 1;
          const key = sub ?? label;
          bySub.set(key, [...(bySub.get(key) || []), row]);
        });
        // Only nest when the sub-level actually separates things. This is what
        // keeps an all-OTHERS project (110, 111 — nothing resolves) from
        // growing a second tier that just repeats the first.
        if (resolved === 0 || bySub.size < 2) return group;

        group.children = Array.from(bySub.entries())
          .map(([subLabel, subLeaves]) =>
            makeGroup(subLabel, subLeaves, `g2::${label}`, label)
          )
          .sort((a, b) => {
            const byCost = b.costNum - a.costNum;
            return byCost !== 0
              ? byCost
              : compareGroupLabels(a.itemName || "", b.itemName || "");
          });
        return group;
      })
      .sort((a, b) => {
        const byCost = b.costNum - a.costNum;
        return byCost !== 0
          ? byCost
          : compareGroupLabels(a.itemName || "", b.itemName || "");
      });
  }, [filteredRows]);

  const allGroupKeys = useMemo(() => {
    const keys: React.Key[] = [];
    const walk = (row: TableRow) => {
      if (!row.isGroup) return;
      keys.push(row.key);
      (row.children || []).forEach(walk);
    };
    groupedRows.forEach(walk);
    return keys;
  }, [groupedRows]);

  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  // Collapse on a fresh dataset: the group headers ARE the overview, and
  // "Expand all" is one click. Deps deliberately exclude `search`, so typing in
  // the box does not fight the user's expand choices.
  useEffect(() => {
    setExpandedRowKeys([]);
  }, [items, sourceFilter, viewMode]);

  const isGrouped = viewMode === "Group";

  // A per-unit cell that a mixed-UOM group cannot honestly roll up.
  const perUnitCell = (value: number, row: TableRow, render: () => React.ReactNode) => {
    if (row.isGroup && row.mixedUom) {
      return (
        <Tooltip title="Items in this group use different units, so a per-unit figure cannot be rolled up. The ₹ columns still total.">
          <span style={{ color: DASH, fontStyle: "italic" }}>mixed</span>
        </Tooltip>
      );
    }
    if (!Number.isFinite(value)) return <span style={{ color: DASH }}>—</span>;
    return render();
  };

  const columns: ColumnsType<TableRow> = [
    {
      title: "#",
      dataIndex: "rank",
      width: 56,
      render: (rank: number, row) =>
        row.isGroup ? null : <span style={{ color: "#8c8c8c" }}>{rank}</span>,
    },
    {
      title: "Labour Item",
      dataIndex: "itemName",
      render: (_: any, row) => {
        if (row.isGroup) {
          return (
            <span>
              <b style={{ color: INK }}>{row.itemName}</b>
              <span style={{ fontSize: 11.5, color: MUTED, marginLeft: 8 }}>
                {row.leafCount} item{row.leafCount === 1 ? "" : "s"}
              </span>
            </span>
          );
        }
        return (
          <div>
            {onItemClick ? (
              <Tooltip title="Monthly schedule — quantity & cost by month">
                <Button
                  type="link"
                  size="small"
                  onClick={() => onItemClick(row)}
                  style={{
                    padding: 0,
                    height: "auto",
                    fontWeight: 500,
                    textAlign: "left",
                    whiteSpace: "normal",
                  }}
                >
                  {row.itemName || "—"}
                  <CalendarOutlined
                    style={{ fontSize: 12, marginLeft: 6, opacity: 0.65 }}
                  />
                </Button>
              </Tooltip>
            ) : (
              <div style={{ fontWeight: 500 }}>{row.itemName || "—"}</div>
            )}
            {row.itemCode ? (
              <div style={{ fontSize: 12, color: "#8c8c8c" }}>{row.itemCode}</div>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "Source",
      dataIndex: "source",
      width: 90,
      render: (source: string, row) => {
        if (row.isGroup) return null;
        return source === "PRW" ? (
          <Tag color="blue" style={{ marginRight: 0 }}>
            PRW
          </Tag>
        ) : (
          <Tag color="orange" style={{ marginRight: 0 }}>
            Others
          </Tag>
        );
      },
    },
    {
      title: "UOM",
      dataIndex: "uomName",
      width: 90,
      render: (uom: string, row) =>
        row.isGroup && row.mixedUom ? (
          <Tooltip title="Items in this group use different units">
            <span style={{ color: DASH, fontStyle: "italic" }}>mixed</span>
          </Tooltip>
        ) : (
          uom || "—"
        ),
    },
    {
      title: "Total Quantity",
      dataIndex: "qtyNum",
      width: 130,
      align: "right",
      sorter: isGrouped ? undefined : (a, b) => a.qtyNum - b.qtyNum,
      render: (qty: number, row) =>
        perUnitCell(qty, row, () => (
          <span
            style={{
              fontVariantNumeric: "tabular-nums",
              fontWeight: row.isGroup ? 600 : undefined,
              color: row.isGroup ? INK : undefined,
            }}
          >
            {fmtQty(qty)}
          </span>
        )),
    },
    {
      title: "Avg Rate",
      dataIndex: "rateNum",
      width: 140,
      align: "right",
      sorter: isGrouped ? undefined : (a, b) => a.rateNum - b.rateNum,
      render: (rate: number, row) =>
        perUnitCell(rate, row, () => (
          <span
            style={{
              fontVariantNumeric: "tabular-nums",
              fontWeight: row.isGroup ? 600 : undefined,
              color: row.isGroup ? INK : undefined,
            }}
          >
            {fmtINR(rate)}
          </span>
        )),
    },
    {
      title: "Total Cost",
      dataIndex: "costNum",
      width: 130,
      align: "right",
      sorter: isGrouped ? undefined : (a, b) => a.costNum - b.costNum,
      defaultSortOrder: isGrouped ? undefined : "descend",
      render: (cost: number, row) => (
        <Tooltip title={fmtINR(cost)}>
          <span
            style={{
              fontWeight: row.isGroup ? 700 : 600,
              color: row.isGroup ? INK : undefined,
              fontVariantNumeric: "tabular-nums",
              cursor: "default",
            }}
          >
            {fmtINRCompact(cost)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: (
        <Tooltip title="Share of total manpower cost. The bar's length is the share and its colour is the BOQ norm group — the same colour that group has in the donut. The letter is the item's ABC class: A covers the first ~80% of spend.">
          <span style={{ borderBottom: "1px dotted #bfbfbf", cursor: "help" }}>
            % Share
          </span>
        </Tooltip>
      ),
      key: "share",
      width: 176,
      render: (_: any, row) => {
        const share = grandTotal > 0 ? (row.costNum / grandTotal) * 100 : 0;
        const familyLabel = row.familyLabel ?? manpowerGroupLabel(row);
        const family = groupFamily(familyByGroup.get(familyLabel));
        // A group row takes its family's base step, its items a lighter step of
        // the same family — the donut's inner / outer ring relationship.
        const barColor = row.isGroup ? family.base : family.leaf;
        const abc = row.isGroup ? undefined : abcByKey.get(row.key);
        const bar = (
          <Progress
            percent={Math.min(100, Math.round(share * 10) / 10)}
            size="small"
            strokeWidth={6}
            showInfo={false}
            strokeColor={barColor}
            trailColor={SHARE_TRACK}
            style={{ flex: 1, minWidth: 56, margin: 0 }}
          />
        );
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Tooltip
              title={
                <>
                  <div>{familyLabel}</div>
                  <div>
                    {fmtINR(row.costNum)} · {share.toFixed(1)}% of total
                  </div>
                  {abc ? (
                    <div style={{ marginTop: 4 }}>{ABC_HINT[abc]}</div>
                  ) : null}
                </>
              }
            >
              {bar}
            </Tooltip>
            <span
              style={{
                fontSize: 12,
                // Text wears text tokens, never the mark's colour — the bar
                // beside it already carries the group identity.
                color: row.isGroup ? INK : "#595959",
                fontWeight: row.isGroup ? 600 : undefined,
                fontVariantNumeric: "tabular-nums",
                width: 42,
                textAlign: "right",
              }}
            >
              {share.toFixed(1)}%
            </span>
            {/* ABC as a letter, not a colour: the bar's colour is already
                spoken for by the group, and a second colour scale in one cell
                would make neither readable. */}
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: abc ? MUTED : "transparent",
                width: 8,
                textAlign: "center",
              }}
            >
              {abc || " "}
            </span>
          </div>
        );
      },
    },
    {
      title: "Tasks",
      dataIndex: "taskCount",
      width: 80,
      align: "right",
      render: (count: number | undefined, row) => {
        // Never a group total: taskCount counts DISTINCT tasks per item, and
        // items in a group share tasks, so summing over-counts.
        if (row.isGroup) {
          return (
            <Tooltip title="Items in a group share tasks, so their task counts cannot be added up. Expand the group to see each item's tasks.">
              <span style={{ color: DASH }}>—</span>
            </Tooltip>
          );
        }
        const value = num(count);
        if (!onTasksClick || !(value > 0)) return value;
        return (
          <Tooltip title="View phase → activity → task breakdown">
            <Button
              type="link"
              size="small"
              onClick={() => onTasksClick(row)}
              style={{
                padding: 0,
                height: "auto",
                fontVariantNumeric: "tabular-nums",
                fontWeight: 600,
              }}
            >
              {value}
            </Button>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <Segmented
          options={["All", "PRW", "Others"]}
          value={sourceFilter}
          onChange={(value) => setSourceFilter(value as SourceFilter)}
        />
        <Tooltip title="Group nests the register by BOQ norm group">
          <Segmented
            options={["Group", "List"]}
            value={viewMode}
            onChange={(value) => setViewMode(value as ViewMode)}
          />
        </Tooltip>
        <Input
          allowClear
          prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
          placeholder="Search labour item / code"
          style={{ width: 300, maxWidth: "100%" }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {/* Rendered in BOTH modes, disabled in List: hiding them resizes the
            toolbar and makes the page jump when the mode changes. */}
        <Space style={{ marginLeft: "auto" }}>
          <Button
            type="link"
            disabled={!isGrouped}
            onClick={() => setExpandedRowKeys(allGroupKeys)}
          >
            Expand all
          </Button>
          <Button
            type="link"
            disabled={!isGrouped}
            onClick={() => setExpandedRowKeys([])}
          >
            Collapse all
          </Button>
        </Space>
      </div>
      <Table<TableRow>
        size="small"
        rowKey="key"
        columns={columns}
        dataSource={isGrouped ? groupedRows : filteredRows}
        summary={() => {
          // Built from the flat LEAF rows via the same aggregate(), so the
          // footer can never disagree with the group rows above it.
          const total: TableRow = {
            key: "grand-total",
            isGroup: true,
            source: "PRW",
            rank: 0,
            itemName: "TOTAL",
            ...aggregate(filteredRows),
          };
          return (
            <Table.Summary fixed>
              <Table.Summary.Row style={{ background: "#eef2f8" }}>
                {columns.map((column, index) => {
                  const key = (column as any).key || `col-${index}`;
                  const dataIndex = (column as any).dataIndex as
                    | keyof TableRow
                    | undefined;
                  const render = (column as any).render;
                  return (
                    <Table.Summary.Cell
                      key={key}
                      index={index}
                      align={(column as any).align}
                    >
                      {dataIndex === "itemName" ? (
                        <b style={{ color: INK }}>
                          TOTAL
                          <span
                            style={{
                              fontSize: 11.5,
                              color: MUTED,
                              marginLeft: 8,
                              fontWeight: 400,
                            }}
                          >
                            {filteredRows.length} item
                            {filteredRows.length === 1 ? "" : "s"}
                          </span>
                        </b>
                      ) : dataIndex && render ? (
                        render(total[dataIndex] as any, total, 0)
                      ) : key === "share" && render ? (
                        render(undefined as any, total, 0)
                      ) : null}
                    </Table.Summary.Cell>
                  );
                })}
              </Table.Summary.Row>
            </Table.Summary>
          );
        }}
        expandable={
          isGrouped
            ? {
                expandedRowKeys,
                onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
              }
            : undefined
        }
        onRow={(row) =>
          row.isGroup ? { style: { background: "#f5f7fa" } } : {}
        }
        // Tree mode shows every row: a tree over a 15-row page would render
        // only the groups on that page and re-open the same group on page 2.
        pagination={
          isGrouped
            ? false
            : {
                pageSize: 15,
                showSizeChanger: false,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }
        }
        scroll={{ x: 1000 }}
      />
    </>
  );
};

export default ManpowerSummaryTable;
